/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export const SigintSensorInputStrategy = {
  SingleMessage: "single_message",
  SlidingWindow: "sliding_window",
} as const;
/**
 * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
 */
export type SigintSensorInputStrategy = OpenEnum<
  typeof SigintSensorInputStrategy
>;

/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export const SigintSensorMode = {
  MultiLabel: "multi_label",
  Exclusive: "exclusive",
  OrderedScore: "ordered_score",
} as const;
/**
 * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
 */
export type SigintSensorMode = OpenEnum<typeof SigintSensorMode>;

/**
 * A project-scoped sensor with ordered live references to custom signals. signal_ids is always present, including when empty.
 */
export type SigintSensor = {
  /**
   * Creation time
   */
  createdAt: Date;
  /**
   * Optional author-facing description
   */
  description?: string | undefined;
  /**
   * Whether the sensor is eligible for evaluation. Disabled sensors remain visible and editable.
   */
  enabled: boolean;
  /**
   * Sensor ID
   */
  id: string;
  /**
   * Message context used for classification. Sliding windows classify the current message with preceding messages ordered by timestamp.
   */
  inputStrategy: SigintSensorInputStrategy;
  /**
   * Optional framing instructions for classification
   */
  instructions?: string | undefined;
  /**
   * Boolean CEL predicate over message.role (lowercase user, assistant, system, or tool)
   */
  matchExpression: string;
  /**
   * How a sensor interprets its ordered signals: independently applicable labels, an exclusive choice, or ordered score levels.
   */
  mode: SigintSensorMode;
  /**
   * Display name
   */
  name: string;
  /**
   * Owning project ID
   */
  projectId: string;
  /**
   * Ordered signal IDs. In ordered_score mode the zero-based array index is the score level.
   */
  signalIds: Array<string>;
  /**
   * A short url-friendly label that uniquely identifies a resource.
   */
  slug: string;
  /**
   * Last update time
   */
  updatedAt: Date;
  /**
   * Sliding window size including the target message, between 2 and 10; defaults to 5
   */
  windowSize: number;
};

/** @internal */
export const SigintSensorInputStrategy$inboundSchema: z.ZodMiniType<
  SigintSensorInputStrategy,
  unknown
> = openEnums.inboundSchema(SigintSensorInputStrategy);

/** @internal */
export const SigintSensorMode$inboundSchema: z.ZodMiniType<
  SigintSensorMode,
  unknown
> = openEnums.inboundSchema(SigintSensorMode);

/** @internal */
export const SigintSensor$inboundSchema: z.ZodMiniType<SigintSensor, unknown> =
  z.pipe(
    z.object({
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      description: z.optional(z.string()),
      enabled: z.boolean(),
      id: z.string(),
      input_strategy: SigintSensorInputStrategy$inboundSchema,
      instructions: z.optional(z.string()),
      match_expression: z.string(),
      mode: SigintSensorMode$inboundSchema,
      name: z.string(),
      project_id: z.string(),
      signal_ids: z.array(z.string()),
      slug: z.string(),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      window_size: z.int(),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "input_strategy": "inputStrategy",
        "match_expression": "matchExpression",
        "project_id": "projectId",
        "signal_ids": "signalIds",
        "updated_at": "updatedAt",
        "window_size": "windowSize",
      });
    }),
  );

export function sigintSensorFromJSON(
  jsonString: string,
): SafeParseResult<SigintSensor, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SigintSensor$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SigintSensor' from JSON`,
  );
}
